import { useCallback, useEffect, useRef, useState } from 'react';

export type LocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

const requestLocation = (setState: (state: LocationPermissionState) => void) =>
  new Promise<void>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('geolocation unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => {
        setState('granted');
        resolve();
      },
      (error) => {
        setState('denied');
        reject(error);
      },
      { timeout: 10000 },
    );
  });

/**
 * Location permission state for the lobby checklist. The lobby asks for it on
 * mount, next to the camera and the microphone: the walkthrough map (CAD-6600)
 * is only as good as the breadcrumbs it gets, and a customer who is already
 * walking the property will not go looking for a permission row. Tracks the
 * live PermissionStatus so a flip in the browser's own site controls updates
 * the row without a reload.
 */
export default function useLocationPermission(enabled: boolean) {
  const [state, setState] = useState<LocationPermissionState>(() =>
    (navigator.geolocation ? 'prompt' : 'unsupported'),
  );

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      return;
    }
    let disposed = false;
    let status: PermissionStatus | undefined;
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (disposed) {
          return;
        }
        status = permissionStatus;
        setState(permissionStatus.state);
        permissionStatus.onchange = () => setState(permissionStatus.state);
      })
      .catch(() => {
        // Permissions API missing (older Safari): stay promptable.
      });
    return () => {
      disposed = true;
      if (status) {
        status.onchange = null;
      }
    };
  }, [enabled]);

  const request = useCallback(() => requestLocation(setState), []);

  const hasAskedOnMount = useRef(false);

  useEffect(function askOnMount() {
    if (!enabled || hasAskedOnMount.current) {
      return;
    }
    hasAskedOnMount.current = true;
    // A refusal is expected and already visible in the checklist row, which
    // offers the retry with its unblock instructions.
    request().catch(() => undefined);
  }, [enabled, request]);

  return { state, request };
}
